/**
 * 🔐 Login Component - Senior Level Implementation
 *
 * Clean, maintainable login component following best practices:
 * - Separation of concerns (UI vs logic)
 * - Custom hook for business logic
 * - Centralized navigation
 * - Proper loading/error states
 * - Type safety
 */

import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { loginStyles as styles } from '../../../../shared/styles/loginStyles';
import { navigateBasedOnArea } from '../../../../App/utils/redirectHelpers';
import { useLogin, type LoginResponse } from '../../hooks';
import useI18n from '../../../../shared/hooks/i18n/useI18n';
import ChangePasswordModal from '../../components/ChangePasswordModal';
import ForgotPasswordModal from '../../components/ForgotPasswordModal';

interface LoginProps {
	onLogin: () => void;
}

const Login: React.FC<LoginProps> = ({ onLogin }) => {
	// Form state
	const [username, setUsername] = useState('');
	const [password, setPassword] = useState('');

	// Modal state
	const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
	const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
	const [loginData, setLoginData] = useState<LoginResponse | null>(null);

	// Hooks
	const navigate = useNavigate();
	const { login, loading, error } = useLogin();
	const { t } = useI18n();

	// ✅ OPTIMIZACIÓN: Memoizar handlers con useCallback
	const handleUsernameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>): void => {
		setUsername(e.target.value);
	}, []);

	const handlePasswordChange = useCallback((e: React.ChangeEvent<HTMLInputElement>): void => {
		setPassword(e.target.value);
	}, []);

	const handleForgotPasswordClick = useCallback((): void => {
		setShowForgotPasswordModal(true);
	}, []);

	const handleCloseForgotPassword = useCallback((): void => {
		setShowForgotPasswordModal(false);
	}, []);

	/**
	 * Handle login form submission
	 */
	const handleSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
		e.preventDefault();

		const result = await login(username, password);

		if (result.success && result.data) {
			// Check if first login - force password change
			if (result.data.firstLogin) {
				setLoginData(result.data);
				setShowChangePasswordModal(true);
				return;
			}

			// Normal login flow
			onLogin();
			navigateBasedOnArea(result.data.area, navigate);
		}
	}, [login, username, password, onLogin, navigate]);

	/**
	 * Handle successful password change after first login
	 */
	const handlePasswordChangeSuccess = useCallback((): void => {
		setShowChangePasswordModal(false);
		onLogin();

		if (loginData) {
			navigateBasedOnArea(loginData.area, navigate);
		}
	}, [onLogin, loginData, navigate]);

	return (
		<>
			<div className={styles.container}>
				<main className={styles.mainContent}>
					<div className={styles.loginBox}>
						{/* Left Section - Branding */}
						<section className={styles.leftSection}>
							<div className={styles.leftContent}>
								<div className={styles.brandingTop}>
									<img
										src='/logo-app.png'
										alt='Mine ESG Logo'
										className={styles.logo}
									/>
									<h1 className={styles.brandTitle}>{t('login.welcomeTitle')}</h1>
									<p className={styles.brandText}>{t('login.welcomeDescription')}</p>
								</div>
								<div className={styles.brandingBottom}>{t('login.copyright')}</div>
							</div>
						</section>

						{/* Right Section - Login Form */}
						<section className={styles.rightSection}>
							<div className={styles.loginForm}>
								<div className={styles.formHeader}>
									<h2 className={styles.formTitle}>{t('login.loginTitle')}</h2>
									<p className={styles.formSubtitle}>{t('login.loginSubtitle')}</p>
								</div>

								{/* Error Message */}
								{error && <div className={styles.errorMessage}>{error}</div>}

								<form onSubmit={handleSubmit} className='space-y-6'>
									{/* Username Input */}
									<div className={styles.inputGroup}>
										<label htmlFor='username' className={styles.inputLabel}>
											{t('login.usernameLabel')}
										</label>
										<input
											id='username'
											type='text'
											value={username}
											onChange={handleUsernameChange}
											className={styles.input}
											placeholder={t('login.usernamePlaceholder')}
											required
											disabled={loading}
										/>
									</div>

									{/* Password Input */}
									<div className={styles.inputGroup}>
										<label htmlFor='password' className={styles.inputLabel}>
											{t('login.passwordLabel')}
										</label>
										<input
											id='password'
											type='password'
											value={password}
											onChange={handlePasswordChange}
											className={styles.input}
											placeholder={t('login.passwordPlaceholder')}
											required
											disabled={loading}
										/>
									</div>

									{/* Submit Button */}
									<button
										type='submit'
										className={styles.loginButton}
										disabled={loading}
									>
										{loading ? t('login.loggingIn') : t('login.loginButton')}
									</button>

									{/* Forgot Password Link */}
									<div className='text-center'>
										<button
											type='button'
											onClick={handleForgotPasswordClick}
											className='text-blue-600 hover:text-blue-800 text-sm font-medium transition-colors duration-200'
											disabled={loading}
										>
											{t('login.forgotPasswordLink')}
										</button>
									</div>

									<div className={styles.divider}>
										<span className={styles.dividerText}>
											{t('login.platformName')}
										</span>
									</div>
								</form>
							</div>
						</section>
					</div>
				</main>
			</div>

			{/* Change Password Modal (First Login) */}
			<ChangePasswordModal
				isOpen={showChangePasswordModal}
				onSuccess={handlePasswordChangeSuccess}
			/>

			{/* Forgot Password Modal */}
			<ForgotPasswordModal
				isOpen={showForgotPasswordModal}
				onClose={handleCloseForgotPassword}
			/>
		</>
	);
};

export default Login;
